import React, { useEffect, useMemo } from 'react';

import '@ygtechnologies/bygr-frontend-component-library/dist/index.css';
import '@ygtechnologies/rubus-ui/dist/index.css';

import { BaseApp, LoadingIndicator, ToastContainer } from '@ygtechnologies/bygr-frontend-component-library';
import {
    RootStyle,
    RubusUIContextData,
    RubusUIProvider,
    rucoriaDarkTheme,
    rucoriaTheme,
    useThemeStyle,
} from '@ygtechnologies/rubus-ui';

import '@/_variables.scss';
import '@/global.scss';
import MainRouting from '@/components/MainRouting/MainRouting';
import { useAsyncState } from '@/utils/hooks';
import { getSystemInfo } from '@/services/system';
import TopEnvironmentBar from '@/components/TopEnvironmentBar';
import { useThemePreference } from '@/components/ThemePreferenceContext';

// point the comp-lib legacy vars at the theme so its components follow dark mode (same as CM)
const darkLegacyOverrides: Record<string, string> = {
    '--color-content-background': 'var(--rui-semantic-surface-elevated)',
    '--color-button': 'var(--rui-semantic-intent-primary-surface)',
    '--color-button-focus': 'var(--rui-semantic-intent-primary-surface-hover)',
    '--color-button-active': 'var(--rui-semantic-intent-primary-surface-active)',
    '--color-button-contrast': 'var(--rui-semantic-intent-primary-text)',
    '--color-button-secondary': 'var(--rui-semantic-intent-tertiary-surface)',
    '--color-button-secondary-contrast': 'var(--rui-semantic-intent-tertiary-text)',
    '--color-button-disabled': 'var(--rui-semantic-intent-disabled-surface)',
    '--color-button-disabled-contrast': 'var(--rui-semantic-intent-disabled-text)',
    // SimpleBarChart bars default to primary-600, which is near-invisible on the dark background;
    // primary-300 is the stop the dark theme itself uses for brand-tinted icons on dark surfaces
    '--chart-bar-color': 'var(--rui-color-primary-300)',
};

const App: React.FC = () => {
    const [systemInfo] = useAsyncState(() => getSystemInfo(), [], null);
    const { themePreference } = useThemePreference();
    const isDark = themePreference === 'dark';
    const { styles } = useThemeStyle(isDark ? rucoriaDarkTheme : rucoriaTheme, { emitLegacyAliases: true });

    // BaseApp's legacy --content-start-left assumes the old 80px sidebar; the rubus Menu
    // offset is applied in MainRouting instead, so BaseApp only pads symmetrically.
    const rootStyles = useMemo(
        () => ({
            ...styles,
            '--content-start-left': 'var(--content-padding)',
            ...(isDark ? darkLegacyOverrides : {}),
        }),
        [styles, isDark]
    );

    const rubusUiData: RubusUIContextData = useMemo(
        () => ({
            currentLocale: 'en',
            texts: {},
        }),
        []
    );

    useEffect(() => {
        document.title = systemInfo?.environmentName ? `${systemInfo?.environmentName} - MacGyver` : 'MacGyver';
    }, [systemInfo]);

    if (!systemInfo) return <LoadingIndicator />;
    return (
        <RubusUIProvider value={rubusUiData}>
            <RootStyle value={rootStyles} />
            {/* BaseApp defaults currentLanguageCode to nb-NO; MacGyver is English-only */}
            <BaseApp currentLanguageCode="en-GB">
                <TopEnvironmentBar systemInfo={systemInfo} />
                <MainRouting />
                <ToastContainer />
            </BaseApp>
        </RubusUIProvider>
    );
};

export default App;
